import { GoogleGenerativeAI } from "@google/generative-ai";
import { GEMINI_AI_KEY } from "../utils/constants";


// Initialize with your API key
const genAI = new GoogleGenerativeAI(GEMINI_AI_KEY);

// Function to convert file to generative part
const fileToGenerativePart = (file: File) => {
  return new Promise((resolve,reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Data=reader.result as string
      resolve({
        inlineData: {
          data: base64Data.split(',')[1], // Extract base64 data
          mimeType: file.type
        }
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const analyzeImageAndGenerateContent = async (imageFile: File) => {
  try {
    const model = genAI.getGenerativeModel({ 
      model: "gemini-1.5-flash",
    });

    // Convert image file to generative part
    const imagePart:any = await fileToGenerativePart(imageFile);

    // Prepare the prompt
    // const prompt = `
    //   Carefully analyze this image and provide the following information in JSON format only:
    //   {
    //     "title": "A concise, creative title for this image (max 1-2 words)",
    //     "blurb": "A short, engaging blurb about the image (max 3-5 words)",
    //     "description": "A detailed description of the image content (max 10-20 words)"
    //   }

    //   Follow these guidelines:
    //   1. Examine all visual elements thoroughly
    //   2. Identify key subjects, colors, composition, and mood
    //   3. Make the title catchy but accurate
    //   4. Keep the blurb engaging and concise
    //   5. Make the description detailed but not verbose
    //   6. Return ONLY the JSON object, no additional text or markdown
    // `;

    // const prompt = `
    //   You are analyzing a Figma UI design image.

    //   Your task is:
    //   1. Provided image could be the whole website any particular section (component), page or mobile screen design image: 
    //   1. Identify which UI section (Component) this image belongs to. It can be one of the following (but not limited to):
    //     - Testimonial, Connect Wallet, Header, Navigation, Image Grid, Sign in, Sign up, Content, Pricing, Upload, Dashboard, Features, Newsletter, Our Work, Blog, Modal, Team, E-Commerce, How it Works, FAQs, 404, Footer, Clients, Review, Contact, Profile, Component Test, etc.
    //   2. Identify which UI section (Page) this image belongs to. It can be one of the following (but not limited to):
    //     - Our Solutions, Listing, Pricing, Community, About Us, Support, Our Services, Mentors, Home, Courses, Blogs, etc.
    //   3. Identify which UI section (Mobile screen) this image belongs to. It can be one of the following (but not limited to):
    //     - About Us, Support, Our Services, Home, Courses, Blogs, etc.
    //   4. Use that section name as the "title"
    //   5. Based on the layout and visuals, generate a creative and relevant "blurb" and "description"

    //   Return the result in Json formate **exactly this format**:

    //   {
    //      "title": "Section name — e.g., Header, Hero, Footer, etc.",
    //      "blurb": "3–5 word engaging summary of the design",
    //      "description": "10–20 word description describing layout, function, and key UI elements"
    //   }
      
    //   Guidelines:
    //     - Observe layout, text, CTA buttons, charts, images, icons, navigation, and section purpose
    //     - Adapt based on the type of section (Hero, Card, Sidebar, Dashboard, etc.)
    //     - The output must reflect the **design’s function** and **visual style**
    //     - The image is a modern SaaS UI (dashboard, landing, or onboarding screen, or any category like, header, login, signup. pricing etc.)
    //     - Focus on layout, components, structure, call-to-action buttons, charts, typography, color scheme, and icons
    //     - Use accurate yet creative wording
    //     - Avoid generic phrases like 'a beautiful UI', 'AI' or 'nice design'
    //     - Do NOT include any markdown or commentary—**return ONLY the JSON**

    //   Strict rules:
    //   - Do NOT return anything except the 3 lines
    //   - Return ONLY the JSON object, no additional text or markdown
    //   - Infer the section type based on visual layout (e.g., top nav + CTA = Header, pricing cards = Pricing)

    //   Example Output:
    //   1.
    //   title: Header  
    //   blurb: Explore Crypto Easily  
    //   description: Access charts, wallets, and real-time data.
    //   2.
    //   title: Features  
    //   blurb: Features That Matter  
    //   description: Access key tools for secure trading.
    //   3.
    //   title: Modal  
    //   blurb: Quick Sign Up  
    //   description: Create account with form or social login.
    //   4.
    //   title: Content  
    //   blurb: Track crypto in real time  
    //   description: Follow price changes live and fast.
    // `;

    const prompt = `
      Analyze this Figma UI design image and determine its category (title), along with a blurb and description.

      ### **Step 1: Identify the UI Section (Title)**
      The image could belong to one of these categories:
      - **Component:** Testimonial, Connect Wallet, Header, Navigation, Image Grid, Sign in, Sign up, Content,Pricing, Upload, 404, Dashboard, Features, Newsletter, Our Work, Blog, Modal, Team, E-Commerce, How it Works, FAQs, 404, Footer, Clients, Review, Contact, Profile, Component Test, etc.
      - **Page:** Our Solutions, Listing, Pricing, Community, About Us, Support, Our Services, Mentors, Home, Courses, Blogs, etc.
      - **Mobile Screen:** About Us, Support, Our Services, Home, Courses, Blogs, Onboarding, Settings, Dashboard, Home, etc.

      ### **Step 2: Generate Blurb & Description**
      - **Blurb (3-5 words):** Engaging, action-driven, or benefit-focused (e.g., "Trade Crypto Securely").
      - **Description (10-20 words):** Explain layout, key elements, and purpose (e.g., "Clean navigation with wallet integration and live crypto charts.").

      ### **Strict Output Format (ONLY JSON)**
      {
        "title": "Exact category name (e.g., Header, Pricing, Modal)",
        "blurb": "Short engaging phrase (3-5 words max)",
        "description": "Concise functional description (10-20 words max)"
      }

      ### **Guidelines for Analysis**
      1. **Check positioning:**  
        - Top of page? Likely **Header**.  
        - Bottom? Likely **Footer**.  
        - Full-width with CTA? Likely **Hero**.  
      2. **Look for key elements:**  
        - Forms? → **Sign In / Sign Up / Contact**  
        - Pricing cards? → **Pricing**  
        - Testimonials? → **Testimonial**  
        - Charts/data? → **Dashboard**  
      3. **Mobile-specific?** → Check if layout is mobile-optimized (e.g., hamburger menu).  

      ### **Strict Rules**
      - **NO markdown, explanations, or extra text**—ONLY the JSON object.  
      - **NO generic phrases** like "modern UI", "AI" or "beautiful design."  
      - **Be precise**—e.g., "Connect Wallet" instead of just "Button."  

      ### **Example Outputs**
      {
        "title": "Header",
        "blurb": "Explore Crypto Easily",
        "description": "Navigation bar with wallet link and live price tracker."
      }
      {
        "title": "Pricing",
        "blurb": "Plans for Everyone",
        "description": "Three-tier pricing cards with feature comparisons and CTA buttons."
      }
      {
        "title": "Modal",
        "blurb": "Quick Sign Up",
        "description": "Popup with email/password fields and social login options."
      }
    `;

    const result = await model.generateContent([prompt, imagePart]);
    const response = await result.response;
    
    // Extract and parse the JSON response
    const text = response.text();
    const jsonStart = text.indexOf('{');
    const jsonEnd = text.lastIndexOf('}') + 1;
    const jsonString = text.slice(jsonStart, jsonEnd);
    
    return JSON.parse(jsonString);
    
  } catch (error:any) {
    console.error("Error calling Gemini API:", error);
    throw new Error(`Failed to analyze image: ${error.message}`);
  }
};